import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Progress } from '../ui';
import { OfflineNotice, RetryPanel, ViewState } from '../states';
import { NextStep } from './NextStep';
import { PathDetail } from './PathDetail';
import { PathOverview } from './PathOverview';
import { useOnboarding } from './useOnboarding';

/**
 * The Onboarding surface (spec §9.4, §10.4).
 *
 * Structure, top to bottom, and why:
 *   1. page header — what this screen is, plus the only write action it has
 *      (planning a voluntary path over the connected source),
 *   2. system state — offline / error / permission / no source, rendered by
 *      the shared state layer so all eight states of §11 look and behave the
 *      same here as everywhere else, and rendered NEXT TO the content rather
 *      than instead of it: progress that already loaded stays readable,
 *   3. the next step — always visible (spec §9.4),
 *   4. overall progress — completion, never activity,
 *   5. either the path overview or one opened path.
 *
 * Which path is open lives in the URL (`?path=…`), so a reload, the back
 * button or a shared link all land in the same place. `lastPathId` in local
 * storage is the fallback for arriving with no query at all — that plus the
 * locally persisted completions is what makes an interrupted session free to
 * resume (spec §9.4, §11.1).
 */
export function OnboardingView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    paths,
    next,
    totals,
    loading,
    error,
    generating,
    generationError,
    canGenerate,
    hasPlan,
    lastPathId,
    reload,
    generate,
    setStepDone,
    rememberPath,
  } = useOnboarding();

  const [announcement, setAnnouncement] = useState('');

  const activeId = searchParams.get('path');
  const activePath = useMemo(
    () => (activeId ? paths.find((path) => path.id === activeId) || null : null),
    [activeId, paths]
  );

  // Arriving with no query: reopen whatever the user last had open, without
  // pushing a history entry they did not ask for.
  useEffect(() => {
    if (activeId || !lastPathId || loading) return;
    if (!paths.some((path) => path.id === lastPathId)) return;
    setSearchParams({ path: lastPathId }, { replace: true });
  }, [activeId, lastPathId, loading, paths, setSearchParams]);

  const openPath = useCallback(
    (path) => {
      rememberPath(path.id);
      setSearchParams({ path: path.id });
    },
    [rememberPath, setSearchParams]
  );

  const backToOverview = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const handleToggle = useCallback(
    (step, done) => {
      setStepDone(step.id, done);
      const completed = Math.max(0, totals.completed + (done ? 1 : -1));
      setAnnouncement(
        `${t(done ? 'onboarding.stepCompleted' : 'onboarding.stepReopened', { title: step.title })} ${t(
          'onboarding.progressAnnouncement',
          { completed, total: totals.total }
        )}`
      );
    },
    [setStepDone, t, totals.completed, totals.total]
  );

  // A reload (manual retry, or the reconnect probe) must not blank a screen
  // that already has content on it — spec §11.1: never lose what is visible.
  const showLoadingState = loading && paths.length === 0;

  const generateButton = (
    <Button
      variant={hasPlan ? 'secondary' : 'primary'}
      size="compact"
      loading={generating}
      disabled={!canGenerate}
      onClick={generate}
    >
      {hasPlan ? t('onboarding.regenerate') : t('onboarding.generate')}
    </Button>
  );

  return (
    <div className="etch-view">
      <div className="etch-column" data-width="wide">
        <header className="etch-page-header">
          <div className="etch-page-header__title">
            <span className="etch-eyebrow">{t('nav.onboarding')}</span>
            <h1>{t('onboarding.title')}</h1>
            <p className="etch-onboarding__lead">{t('onboarding.lead')}</p>
          </div>
          <div className="etch-page-header__actions">{generateButton}</div>
        </header>

        {/* Progress and step changes are announced here; focus never moves
            (spec §12 "Statusmeldungen ohne unerwarteten Fokuswechsel"). */}
        <p className="etch-visually-hidden" role="status" aria-live="polite">
          {announcement}
        </p>

        <div className="etch-stack" data-gap="lg">
          {/* Watches the connection itself and re-runs the load once it is
              back, so a reconnect does not need a manual refresh. */}
          <OfflineNotice description={t('onboarding.offlineBody')} onReconnect={reload} />

          {error && (
            <RetryPanel
              compact
              error={error}
              onRetry={reload}
              retrying={loading}
              description={t('onboarding.loadFailedBody')}
            />
          )}

          {generationError && (
            <Alert tone="warning" title={t('onboarding.generationFailed')}>
              {generationError.code === 'generation_empty'
                ? t('onboarding.emptyPlanError')
                : t('onboarding.loadFailedBody')}
            </Alert>
          )}

          {/* Explains the disabled "generate" button — but only while there
              is nothing generated yet, so it does not nag someone who already
              has a path and simply left their workspace. */}
          {!canGenerate && !hasPlan && !loading && (
            <ViewState
              compact
              status="noSource"
              description={t('onboarding.noSourceBody')}
              onConnectSource={() => navigate('/chat')}
            />
          )}

          {generating && <ViewState compact status="loading" title={t('onboarding.loading')} description={t('onboarding.loadingHint')} />}

          {showLoadingState ? (
            <ViewState status="loading" description={t('onboarding.loadingHint')} />
          ) : (
            <>
              <NextStep next={next} onOpenPath={openPath} />

              {totals.total > 0 && (
                <div className="etch-path-progress" data-scope="all">
                  <Progress
                    value={totals.completed}
                    max={Math.max(totals.total, 1)}
                    label={t('onboarding.progressOf', { completed: totals.completed, total: totals.total })}
                    showValue={false}
                  />
                  {totals.requiredTotal > 0 && (
                    <p className="etch-path-progress__required">
                      {t('onboarding.requiredProgress', {
                        completed: totals.requiredCompleted,
                        total: totals.requiredTotal,
                      })}
                    </p>
                  )}
                  <p className="etch-path-progress__note">{t('onboarding.progressNote')}</p>
                </div>
              )}

              {activeId ? (
                <PathDetail
                  path={activePath}
                  onBack={backToOverview}
                  onToggle={handleToggle}
                  onReload={reload}
                />
              ) : (
                <PathOverview
                  paths={paths}
                  onOpen={openPath}
                  generateAction={generateButton}
                  optionalEmptyBody={canGenerate ? t('onboarding.emptyBody') : t('onboarding.noSourceBody')}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingView;
